import { Container, IDestroyOptions, Point } from "pixi.js";
import gsap from "gsap";
import { MyWalletScrollBox } from "../myWalletNFTs/MyWalletScrollBox";
import { Event } from "event-system";
import { WalletTabType } from "../WalletTabType";
import { NFTScrollItem } from "../myWalletNFTs/scrollItems/NFTScrollItem";

export abstract class MyWalletTab extends Container {
    protected static readonly ANIMATION_DURATION = 0.15;

    private readonly isScrollable: boolean;

    protected readonly animation: gsap.core.Timeline;
    protected readonly scrollBox?: MyWalletScrollBox;
    protected readonly type: WalletTabType;

    public get scrollable(): boolean {
        return this.isScrollable;
    }

    public get scrollElements(): number {
        return this.scrollBox?.itemsCount;
    }

    public get currentScrollItemIndex(): number {
        return this.scrollBox?.currentItemIndex;
    }

    public get onScrollItemsCountChange(): Event<number> {
        return this.scrollBox?.onItemsCountChange;
    }

    public readonly onItemClick: Event<WalletTabType, NFTScrollItem>;
    public readonly isHadNFT: Event<WalletTabType, boolean>;

    public constructor(isScrollable: boolean, type: WalletTabType, scrollOptions?: MyWalletTabScrollOptions) {
        super();
        this.isScrollable = isScrollable;
        this.type = type;
        this.animation = new gsap.core.Timeline({ paused: true });
        this.isHadNFT = new Event();
        this.onItemClick = new Event();
        if (isScrollable) {
            this.scrollBox = new MyWalletScrollBox(scrollOptions.elementsWidth, scrollOptions.elementsMargin);
            this.scrollBox.position.set(scrollOptions.scrollPosition.x, scrollOptions.scrollPosition.y);
            this.addChild(this.scrollBox);
        }
    }

    public async show(): Promise<void> {
        this.animation.fromTo(this, { alpha: 0 }, { alpha: 1, duration: MyWalletTab.ANIMATION_DURATION, ease: 'power1.inOut' });
        await this.animation.play();
        this.animation.clear();
    }

    public async hide(): Promise<void> {
        this.animation.fromTo(this, { alpha: 1 }, { alpha: 0, duration: MyWalletTab.ANIMATION_DURATION, ease: 'power1.inOut' });
        await this.animation.play();
        this.animation.clear();
    }

    public async scrollLeft(): Promise<void> {
        if (this.isScrollable) {
            await this.scrollBox.scrollLeft();
        }
    }

    public async scrollRight(): Promise<void> {
        if (this.isScrollable) {
            await this.scrollBox.scrollRight();
        }
    }

    public abstract updatePointer(): void;

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.animation.kill();
        super.destroy(options);
    }
}

export type MyWalletTabScrollOptions = {
    elementsWidth: number;
    elementsMargin: number;
    scrollPosition: Point;
}
